import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import { useUpdatePronunciationDictionaryItemMutation } from '@circuitry-ai/doc-data';

export interface DictionaryEntryModalContext {
  dictionaryName: string;
  text?: string;
  pronunciation?: string;
}

interface DictionaryEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'create' | 'edit';
  context: DictionaryEntryModalContext;
}

const DictionaryEntryModal: React.FC<DictionaryEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  context,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [originalText, setOriginalText] = useState('');

  const [updateItem, { isLoading }] = useUpdatePronunciationDictionaryItemMutation();

  useEffect(() => {
    if (!isOpen) return;
    setText(context.text ?? '');
    setPronunciation(context.pronunciation ?? '');
    setOriginalText(context.text ?? '');
  }, [context.pronunciation, context.text, isOpen]);

  const handleSubmit = useCallback(async () => {
    const itemText = text.trim();
    const itemPronunciation = pronunciation.trim();
    if (!itemText || !itemPronunciation) {
      CtyShowToast(checkType.error, t('Word and pronunciation are required.'));
      return;
    }

    try {
      await updateItem({
        name: context.dictionaryName,
        item: { text: itemText, pronunciation: itemPronunciation },
      }).unwrap();
      CtyShowToast(
        checkType.success,
        mode === 'edit'
          ? t('Entry updated successfully.')
          : t('Entry added successfully.')
      );
      onSuccess();
      onClose();
    } catch (err) {
      CtyShowToast(
        checkType.error,
        (err as { data?: { message?: string } })?.data?.message ||
          (err as Error)?.message ||
          t('Failed to save entry.')
      );
    }
  }, [context.dictionaryName, mode, onClose, onSuccess, pronunciation, t, text, updateItem]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{mode === 'edit' ? t('Edit entry') : t('Add entry')}</ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label={t('Word')}
            placeholder={t('Word or phrase')}
            value={text}
            onValueChange={setText}
            variant="bordered"
            isRequired
          />
          <Input
            label={t('Pronunciation')}
            placeholder={t('IPA or phonetic spelling')}
            value={pronunciation}
            onValueChange={setPronunciation}
            variant="bordered"
            isRequired
          />
          {mode === 'edit' && originalText && originalText !== text.trim() && (
            <p className="text-xs text-default-500">
              {t('If this word already exists, the backend will replace that entry.')}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="light" onPress={onClose} isDisabled={isLoading}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="primary" onPress={() => void handleSubmit()} isLoading={isLoading}>
            {mode === 'edit' ? t('Save') : t('Add')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DictionaryEntryModal;
